import * as fs from 'node:fs';
import { CONFIG_DIR, CONFIG_FILE } from './config.js';

const KEYRING_SERVICE = 'rekor';
const KEY_REC = 'rec_token';
const KEY_OAUTH_ACCESS = 'oauth_access_token';
const KEY_OAUTH_REFRESH = 'oauth_refresh_token';

interface KeyringBinding {
  getPassword(service: string, account: string): Promise<string | null>;
  setPassword(service: string, account: string, password: string): Promise<void>;
  deletePassword(service: string, account: string): Promise<boolean>;
}

let keyringCache: KeyringBinding | null | undefined;

async function getKeyring(): Promise<KeyringBinding | null> {
  if (keyringCache !== undefined) return keyringCache;
  try {
    const mod = await import('@napi-rs/keyring');
    const Entry = mod.Entry;
    keyringCache = {
      async getPassword(service, account) {
        try { return new Entry(service, account).getPassword(); } catch { return null; }
      },
      async setPassword(service, account, password) {
        new Entry(service, account).setPassword(password);
      },
      async deletePassword(service, account) {
        try { return new Entry(service, account).deletePassword(); } catch { return false; }
      },
    };
  } catch {
    keyringCache = null;
  }
  return keyringCache;
}

function manualCleanupCommand(account: string): string {
  if (process.platform === 'darwin') {
    return `security delete-generic-password -s ${KEYRING_SERVICE} -a ${account}`;
  }
  if (process.platform === 'win32') {
    return `cmdkey /delete:${KEYRING_SERVICE}/${account}`;
  }
  if (process.platform === 'linux') {
    return `secret-tool clear service ${KEYRING_SERVICE} account ${account}`;
  }
  return `(remove service="${KEYRING_SERVICE}" account="${account}" from your OS credential store)`;
}

interface LegacyConfigFile {
  api_url?: string;
  token?: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
  default_database?: string;
  org_id?: string;
  [key: string]: unknown;
}

function readConfigFile(): LegacyConfigFile | null {
  if (!fs.existsSync(CONFIG_FILE)) return null;
  try {
    return JSON.parse(fs.readFileSync(CONFIG_FILE, 'utf-8')) as LegacyConfigFile;
  } catch {
    return null;
  }
}

function writeConfigFile(data: LegacyConfigFile): void {
  if (!fs.existsSync(CONFIG_DIR)) {
    fs.mkdirSync(CONFIG_DIR, { recursive: true, mode: 0o700 });
  }
  fs.writeFileSync(CONFIG_FILE, JSON.stringify(data, null, 2), { mode: 0o600 });
}

// Delete reported success but get still returned a value — the keychain ignored the delete.
export class StaleKeyringSlotError extends Error {
  constructor(public readonly account: string) {
    super(
      `Stale credential remains in OS keychain slot "${account}" after delete. ` +
      `Clean it up manually and retry:\n  ${manualCleanupCommand(account)}`,
    );
    this.name = 'StaleKeyringSlotError';
  }
}

async function clearSlot(keyring: KeyringBinding, account: string): Promise<boolean> {
  await keyring.deletePassword(KEYRING_SERVICE, account);
  try {
    return (await keyring.getPassword(KEYRING_SERVICE, account)) === null;
  } catch {
    return false;
  }
}

export type ResolvedToken =
  | { kind: 'rec'; token: string }
  | { kind: 'oauth'; access_token: string; refresh_token?: string; expires_at?: string };

function stripTokenFieldsFromFile(): void {
  const existing = readConfigFile();
  if (!existing) return;
  if (existing.token === undefined && existing.access_token === undefined && existing.refresh_token === undefined) return;
  delete existing.token;
  delete existing.access_token;
  delete existing.refresh_token;
  writeConfigFile(existing);
}

function readMetadataExpiresAt(): string | undefined {
  return readConfigFile()?.token_expires_at;
}

function writeMetadataExpiresAt(value: string | undefined): void {
  const existing = readConfigFile() ?? {};
  if (value === undefined) {
    delete existing.token_expires_at;
  } else {
    existing.token_expires_at = value;
  }
  writeConfigFile(existing);
}

async function migrateLegacyFileToken(keyring: KeyringBinding | null): Promise<ResolvedToken | null> {
  const existing = readConfigFile();
  if (!existing) return null;

  // Prefer OAuth fields if present (newer file-fallback format), else legacy `token`.
  if (existing.access_token) {
    const out: ResolvedToken = {
      kind: 'oauth',
      access_token: existing.access_token,
      refresh_token: existing.refresh_token,
      expires_at: existing.token_expires_at,
    };
    if (keyring) {
      try {
        // Keyring wins on conflict — only migrate if slots are empty.
        const accessExisting = await keyring.getPassword(KEYRING_SERVICE, KEY_OAUTH_ACCESS);
        if (!accessExisting) {
          await keyring.setPassword(KEYRING_SERVICE, KEY_OAUTH_ACCESS, existing.access_token);
          if (existing.refresh_token) {
            await keyring.setPassword(KEYRING_SERVICE, KEY_OAUTH_REFRESH, existing.refresh_token);
          }
        }
        try { stripTokenFieldsFromFile(); } catch (err) {
          console.warn(`rekor: failed to strip legacy OAuth tokens from ${CONFIG_FILE} (${(err as Error).message}).`);
        }
      } catch (err) {
        console.warn(`rekor: OAuth migration to keychain failed (${(err as Error).message}); continuing with file-backed.`);
      }
    }
    return out;
  }

  if (existing.token) {
    const token = existing.token;
    if (!keyring) return { kind: 'rec', token };
    try {
      const recExisting = await keyring.getPassword(KEYRING_SERVICE, KEY_REC);
      if (recExisting) {
        // Keyring wins — strip stale file copy.
        try { stripTokenFieldsFromFile(); } catch (err) {
          console.warn(`rekor: failed to strip stale token from ${CONFIG_FILE} (${(err as Error).message}).`);
        }
        return null;
      }
      await keyring.setPassword(KEYRING_SERVICE, KEY_REC, token);
      try { stripTokenFieldsFromFile(); } catch (err) {
        console.warn(`rekor: failed to strip legacy token from ${CONFIG_FILE} (${(err as Error).message}).`);
      }
    } catch (err) {
      console.warn(`rekor: keychain migration failed (${(err as Error).message}); continuing with file-backed token.`);
    }
    return { kind: 'rec', token };
  }

  return null;
}

// Read order: REKOR_TOKEN env → rec_ slot → OAuth slot. rec_ wins so an OAuth fallback can't silently switch identities.
export async function getResolvedToken(): Promise<ResolvedToken | null> {
  const envToken = process.env['REKOR_TOKEN'];
  if (envToken) {
    // Always rec-kind — preserves `REKOR_TOKEN=dev` for DEV_BYPASS_AUTH and avoids the OAuth refresh path on plain strings.
    return { kind: 'rec', token: envToken };
  }

  const keyring = await getKeyring();
  const migrated = await migrateLegacyFileToken(keyring);
  if (migrated) return migrated;

  if (keyring) {
    // All three slots in parallel — rec_ wins read precedence but probing sequentially would block on empty slots.
    const [rec, access, refresh] = await Promise.all([
      keyring.getPassword(KEYRING_SERVICE, KEY_REC),
      keyring.getPassword(KEYRING_SERVICE, KEY_OAUTH_ACCESS),
      keyring.getPassword(KEYRING_SERVICE, KEY_OAUTH_REFRESH),
    ]);
    if (rec) return { kind: 'rec', token: rec };
    if (access) {
      const out: ResolvedToken = { kind: 'oauth', access_token: access };
      if (refresh) out.refresh_token = refresh;
      const expiresAt = readMetadataExpiresAt();
      if (expiresAt) out.expires_at = expiresAt;
      return out;
    }
  }

  return null;
}

// Backwards-compatible single-string accessor for callers that don't need the kind.
export async function getToken(): Promise<string | null> {
  const resolved = await getResolvedToken();
  if (!resolved) return null;
  return resolved.kind === 'rec' ? resolved.token : resolved.access_token;
}

export async function setToken(token: string): Promise<void> {
  await setRecToken(token);
}

export async function setRecToken(token: string): Promise<void> {
  const keyring = await getKeyring();
  if (keyring) {
    try {
      // Clear OAuth slots first — a stale OAuth would survive `rekor logout` and confuse mode-switch debugging.
      const [accessCleared, refreshCleared] = await Promise.all([
        clearSlot(keyring, KEY_OAUTH_ACCESS),
        clearSlot(keyring, KEY_OAUTH_REFRESH),
      ]);
      const stuck = [
        accessCleared ? null : KEY_OAUTH_ACCESS,
        refreshCleared ? null : KEY_OAUTH_REFRESH,
      ].filter((a): a is string => a !== null);
      if (stuck.length > 0) {
        console.warn(
          [
            `rekor: stale OAuth credential remains in OS keychain after delete. Commands work but logout won't fully clear. Manually:`,
            ...stuck.map(a => `  ${manualCleanupCommand(a)}`),
          ].join('\n'),
        );
      }
      await keyring.setPassword(KEYRING_SERVICE, KEY_REC, token);
      writeMetadataExpiresAt(undefined);
      try { stripTokenFieldsFromFile(); } catch (err) {
        console.warn(`rekor: failed to strip stale tokens from ${CONFIG_FILE} (${(err as Error).message}).`);
      }
      return;
    } catch (err) {
      if (err instanceof StaleKeyringSlotError) throw err;
      console.warn(`rekor: keychain write failed (${(err as Error).message}); falling back to file storage.`);
    }
  }
  const existing = readConfigFile() ?? {};
  delete existing.access_token;
  delete existing.refresh_token;
  delete existing.token_expires_at;
  existing.token = token;
  writeConfigFile(existing);
}

export async function setOAuthTokens(
  accessToken: string,
  refreshToken: string | undefined,
  expiresAt: string,
): Promise<void> {
  const keyring = await getKeyring();
  if (keyring) {
    try {
      // rec_ wins the read race — a leftover would mask new OAuth tokens; verify empty after delete and fail loudly.
      if (!(await clearSlot(keyring, KEY_REC))) {
        throw new StaleKeyringSlotError(KEY_REC);
      }
      await keyring.setPassword(KEYRING_SERVICE, KEY_OAUTH_ACCESS, accessToken);
      if (refreshToken) {
        await keyring.setPassword(KEYRING_SERVICE, KEY_OAUTH_REFRESH, refreshToken);
      } else {
        // Best-effort clear of any stale refresh slot when the new pair has none.
        await clearSlot(keyring, KEY_OAUTH_REFRESH);
      }
      writeMetadataExpiresAt(expiresAt);
      try { stripTokenFieldsFromFile(); } catch (err) {
        console.warn(`rekor: failed to strip stale tokens from ${CONFIG_FILE} (${(err as Error).message}).`);
      }
      return;
    } catch (err) {
      // StaleKeyringSlotError propagates — file fallback won't help if the keyring still holds rec_.
      if (err instanceof StaleKeyringSlotError) throw err;
      console.warn(`rekor: keychain write failed (${(err as Error).message}); falling back to file storage.`);
    }
  }
  const existing = readConfigFile() ?? {};
  delete existing.token;
  existing.access_token = accessToken;
  if (refreshToken) existing.refresh_token = refreshToken;
  existing.token_expires_at = expiresAt;
  writeConfigFile(existing);
}

export async function clearToken(): Promise<void> {
  await clearAllTokens();
}

export async function clearAllTokens(): Promise<void> {
  const keyring = await getKeyring();
  if (keyring) {
    const slots = [KEY_REC, KEY_OAUTH_ACCESS, KEY_OAUTH_REFRESH] as const;
    const cleared = await Promise.all(slots.map(s => clearSlot(keyring, s)));
    for (const [i, ok] of cleared.entries()) {
      if (!ok) {
        const account = slots[i]!;
        console.warn(
          `rekor: could not clear keychain slot "${account}". Subsequent commands may still pick the stale token. Manually:\n  ${manualCleanupCommand(account)}`,
        );
      }
    }
  }
  const existing = readConfigFile();
  if (existing) {
    let dirty = false;
    for (const k of ['token', 'access_token', 'refresh_token', 'token_expires_at'] as const) {
      if (existing[k] !== undefined) {
        delete existing[k];
        dirty = true;
      }
    }
    if (dirty) writeConfigFile(existing);
  }
}

export function __setKeyringForTesting(binding: KeyringBinding | null): void {
  keyringCache = binding;
}

export function __resetKeyringForTesting(): void {
  keyringCache = undefined;
}
